import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { assets } from '../constants/theme';

export default function HeroSection() {
  const headingRef = useRef(null);
  const subRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const els = [headingRef.current, subRef.current, ctaRef.current];
    const delays = [0.3, 0.55, 0.8];
    els.forEach((el, i) => {
      if (!el) return;
      setTimeout(() => el.classList.add('fadeIn'), delays[i] * 1000);
    });
  }, []);

  const s = {
    section: {
      position: 'relative',
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'flex-end',
      overflow: 'hidden',
      backgroundColor: '#2B1509',
    },
    bg: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `url('${assets.heroBg}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center 30%',
      animation: 'heroZoom 18s ease-out forwards',
    },
    overlay: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(to top, rgba(27,13,7,0.95) 0%, rgba(27,13,7,0.5) 50%, rgba(27,13,7,0.15) 100%)',
    },
    content: {
      position: 'relative',
      zIndex: 2,
      width: '100%',
      padding: '0 6% 7rem',
      display: 'grid',
      gap: '3rem',
    },
    eyebrow: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: '#D96D29',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    eyebrowLine: {
      display: 'inline-block',
      width: '32px',
      height: '1px',
      backgroundColor: '#D96D29',
    },
    heading: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(3rem, 6.5vw, 5.8rem)',
      fontWeight: 400,
      lineHeight: 1.05,
      color: '#F4EAE0',
      letterSpacing: '-0.01em',
    },
    accentLine: {
      display: 'block',
      fontStyle: 'italic',
      color: '#D96D29',
    },
    right: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem',
    },
    body: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: 'clamp(0.9rem, 1.3vw, 1.05rem)',
      fontWeight: 300,
      color: 'rgba(244,234,224,0.88)',
      lineHeight: 1.8,
      maxWidth: '460px',
    },
    ctaGroup: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    ctaPrimary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.6rem',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.78rem',
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: '#F4EAE0',
      backgroundColor: '#A63C1E',
      border: 'none',
      padding: '0.95rem 2.2rem',
      borderRadius: '2px',
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
    },
    ctaSecondary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.6rem',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.78rem',
      fontWeight: 500,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: 'rgba(244,234,224,0.8)',
      border: '1px solid rgba(244,234,224,0.3)',
      backgroundColor: 'transparent',
      padding: '0.95rem 2.2rem',
      borderRadius: '2px',
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
    },
    tagline: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(0.85rem, 1.2vw, 1rem)',
      fontStyle: 'italic',
      color: 'rgba(244,234,224,0.5)',
      marginTop: '0.5rem',
    },
    scrollHint: {
      position: 'absolute',
      bottom: '2.5rem',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.4rem',
      color: 'rgba(244,234,224,0.5)',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.65rem',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      zIndex: 3,
    },
    scrollLine: {
      width: '1px',
      height: '48px',
      background: 'linear-gradient(to bottom, rgba(217,109,41,0.9), transparent)',
      animation: 'scrollPulse 2s ease-in-out infinite',
    },
  };

  return (
    <>
      <style>{`
        @keyframes heroZoom {
          from { transform: scale(1.08); }
          to   { transform: scale(1); }
        }
        @keyframes scrollPulse {
          0%, 100% { opacity: 0.4; transform: scaleY(1); }
          50%       { opacity: 1;   transform: scaleY(1.15); }
        }
      `}</style>
      <section id="hero" style={s.section} aria-label="Hero section">
        <div style={s.bg} aria-hidden="true" />
        <div style={s.overlay} aria-hidden="true" />

        <div style={s.content} className="hero-content">
          <div>
            <div className="preFade" ref={headingRef}>
              <div style={s.eyebrow}>
                <span style={s.eyebrowLine}></span>
                African Specialty Coffee
              </div>
              <h1 className="hero-heading" style={s.heading}>
                Your Cup!
                <span style={s.accentLine}>Your Day!</span>
              </h1>
            </div>
          </div>

          <div style={s.right} className="hero-right">
            <p ref={subRef} className="preFade" style={s.body}>
              At Savana Sips, it's all about your coffee cup, your day. Every sip
              of our specialty-grade Kenyan coffee tells a story — of volcanic soils,
              dedicated farmers, and a community growing stronger with every cup.
            </p>
            <div ref={ctaRef} className="preFade" style={s.ctaGroup}>
              <Link to="/products" style={s.ctaPrimary}
                id="hero-cta-shop"
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#D96D29'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#A63C1E'}
              >
                Shop Our Coffee ↓
              </Link>
              <Link to="/coffee-types" style={s.ctaSecondary}
                id="hero-cta-learn"
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(244,234,224,0.7)'; e.currentTarget.style.color = '#F4EAE0'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(244,234,224,0.3)'; e.currentTarget.style.color = 'rgba(244,234,224,0.8)'; }}
              >
                Explore Coffee types
              </Link>
            </div>
            <div style={s.tagline}>
              "Every cup supports a farmer. Every day builds a community."
            </div>
          </div>
        </div>

        <div className="hero-scroll-hint" style={s.scrollHint} aria-hidden="true">
          <div style={s.scrollLine} />
          <span>Scroll</span>
        </div>
      </section>
    </>
  );
}
